import React, { useState, useEffect, useRef } from 'react'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import aiService from '../../lib/aiService'

const { FiMessageCircle, FiSend, FiX, FiZap, FiRefreshCw, FiCopy, FiCheck } = FiIcons

const AIAssistant = ({ slide, onUpdateSlide, onAddElement, isOpen, onClose }) => {
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const [suggestions, setSuggestions] = useState([])
  const messagesEndRef = useRef(null)

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      // Initialize with welcome message
      setMessages([
        {
          id: 1,
          type: 'ai',
          content: "Hi! I'm your AI assistant. I can help you improve your slides, suggest content, or answer questions about your presentation. What would you like to work on?",
          timestamp: new Date()
        }
      ])
      
      // Generate contextual suggestions
      generateSuggestions()
    }
  }, [isOpen, slide])

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  const generateSuggestions = () => {
    const contextSuggestions = [
      "Improve this slide's visual hierarchy",
      "Suggest better color combinations",
      "Add engaging transitions between points",
      "Make the content more compelling",
      "Optimize for audience engagement"
    ]
    setSuggestions(contextSuggestions)
  }

  const handleSendMessage = async () => {
    if (!input.trim()) return

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: input.trim(),
      timestamp: new Date()
    }

    setMessages(prev => [...prev, userMessage])
    setInput('')
    setIsTyping(true)

    try {
      // Use AI service for response
      const aiResponseData = await aiService.generateAssistantResponse(
        userMessage.content, 
        slide
      )
      
      const aiResponse = {
        id: Date.now(),
        type: 'ai',
        content: aiResponseData.content,
        actions: aiResponseData.actions,
        timestamp: new Date()
      }

      setMessages(prev => [...prev, aiResponse])
    } catch (error) {
      console.error('Error getting AI response:', error)
      
      // Fallback to mock response
      const aiResponse = generateAIResponse(userMessage.content)
      setMessages(prev => [...prev, aiResponse])
    } finally {
      setIsTyping(false)
    }
  }

  const generateAIResponse = (userInput) => {
    const responses = {
      'improve': {
        content: "Here are some suggestions to improve your slide:\n\n• **Visual Hierarchy**: Make your title 20% larger and use a bold font weight\n• **Color Contrast**: Your current text could be more readable - try using #1f2937 for better contrast\n• **Content Structure**: Consider breaking long paragraphs into bullet points\n• **White Space**: Add more padding around elements for a cleaner look\n\nWould you like me to apply any of these changes automatically?",
        actions: ['Apply Better Typography', 'Improve Colors', 'Restructure Content']
      },
      'color': {
        content: "Great question about colors! Based on your current slide:\n\n• **Primary**: Use #2563eb for headers and key elements\n• **Secondary**: #6b7280 works well for body text\n• **Accent**: #f59e0b for highlights and call-to-actions\n• **Background**: Consider a subtle gradient from #f8fafc to #f1f5f9\n\nThese colors follow accessibility guidelines and create good contrast.",
        actions: ['Apply Color Scheme', 'See More Options']
      },
      'content': {
        content: "I can help make your content more engaging:\n\n• **Hook**: Start with a compelling question or statistic\n• **Structure**: Use the 3-point rule - maximum 3 main ideas per slide\n• **Storytelling**: Frame information as a narrative with conflict and resolution\n• **Call-to-Action**: End with a clear next step for your audience\n\nWhat type of content would you like help with?",
        actions: ['Rewrite Content', 'Add Statistics', 'Create Hook']
      }
    }

    // Simple keyword matching for demo
    const inputLower = userInput.toLowerCase()
    let response = responses['improve'] // default

    if (inputLower.includes('color') || inputLower.includes('colour')) {
      response = responses['color']
    } else if (inputLower.includes('content') || inputLower.includes('text') || inputLower.includes('writing')) {
      response = responses['content']
    }

    return {
      id: Date.now(),
      type: 'ai',
      content: response.content,
      actions: response.actions,
      timestamp: new Date()
    }
  }

  const handleSuggestionClick = (suggestion) => {
    setInput(suggestion)
  }

  const handleActionClick = async (action) => {
    const actionMessage = {
      id: Date.now(),
      type: 'user',
      content: action,
      timestamp: new Date()
    }

    setMessages(prev => [...prev, actionMessage])
    setIsTyping(true)

    try {
      let improvementType = 'general'
      let response = "I've applied the changes to your slide! "
      
      if (action.includes('Typography')) {
        improvementType = 'typography'
        response += "I've improved the font sizes and weights for better readability."
      } else if (action.includes('Colors') || action.includes('Color')) {
        improvementType = 'color'
        response += "I've updated the color scheme for better contrast and visual appeal."
      } else if (action.includes('Content')) {
        improvementType = 'content'
        response += "I've restructured the content for better flow and engagement."
      } else if (action.includes('Layout')) {
        improvementType = 'layout'
        response += "I've optimized the layout for better visual hierarchy."
      }

      // Use AI to improve the slide
      if (slide) {
        const improvedSlide = await aiService.improveSlideContent(slide, improvementType)
        if (improvedSlide && improvedSlide !== slide) {
          onUpdateSlide(improvedSlide)
          response += " The changes have been applied to your slide."
        }
      } else {
        // Fallback to manual improvements
        if (action.includes('Typography')) {
          applyTypographyImprovements()
        } else if (action.includes('Colors')) {
          applyColorImprovements()
        } else if (action.includes('Content')) {
          applyContentImprovements()
        }
      }

      const aiResponse = {
        id: Date.now(),
        type: 'ai',
        content: response,
        timestamp: new Date()
      }

      setMessages(prev => [...prev, aiResponse])
    } catch (error) {
      console.error('Error applying action:', error)
      
      // Fallback to manual improvements
      let response = "I've applied the changes to your slide! "
      
      if (action.includes('Typography')) {
        response += "I've improved the font sizes and weights for better readability."
        applyTypographyImprovements()
      } else if (action.includes('Colors')) {
        response += "I've updated the color scheme for better contrast and visual appeal."
        applyColorImprovements()
      } else if (action.includes('Content')) {
        response += "I've restructured the content for better flow and engagement."
        applyContentImprovements()
      }

      const aiResponse = {
        id: Date.now(),
        type: 'ai',
        content: response,
        timestamp: new Date()
      }

      setMessages(prev => [...prev, aiResponse])
    } finally {
      setIsTyping(false)
    }
  }

  const applyTypographyImprovements = () => {
    // Find title elements and improve typography
    if (slide && slide.elements) {
      const updatedElements = slide.elements.map(element => {
        if (element.type === 'text' && parseInt(element.style.fontSize) > 30) {
          return {
            ...element,
            style: {
              ...element.style,
              fontSize: `${Math.max(36, parseInt(element.style.fontSize) * 1.2)}px`,
              fontWeight: 'bold',
              lineHeight: '1.2'
            }
          }
        }
        return element
      })

      onUpdateSlide({ elements: updatedElements })
    }
  }

  const applyColorImprovements = () => {
    if (slide && slide.elements) {
      const updatedElements = slide.elements.map(element => {
        if (element.type === 'text') {
          const fontSize = parseInt(element.style.fontSize)
          return {
            ...element,
            style: {
              ...element.style,
              color: fontSize > 30 ? '#1f2937' : '#374151'
            }
          }
        }
        return element
      })

      onUpdateSlide({ 
        elements: updatedElements,
        background: {
          type: 'gradient',
          value: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)'
        }
      })
    }
  }

  const applyContentImprovements = () => {
    if (slide && slide.elements) {
      const updatedElements = slide.elements.map(element => {
        if (element.type === 'text' && element.content.length > 100) {
          // Break long content into bullet points
          const sentences = element.content.split('. ')
          if (sentences.length > 2) {
            return {
              ...element,
              type: 'list',
              content: sentences.slice(0, 3).map(s => s.replace(/\.$/, ''))
            }
          }
        }
        return element
      })

      onUpdateSlide({ elements: updatedElements })
    }
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed right-4 bottom-4 w-96 h-[500px] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col z-50">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-blue-600 rounded-full flex items-center justify-center">
            <SafeIcon icon={FiZap} className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">AI Assistant</h3>
            <p className="text-xs text-green-600 flex items-center space-x-1">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span>Online</span>
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <SafeIcon icon={FiX} className="w-4 h-4 text-gray-500" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[80%] p-3 rounded-2xl ${
                message.type === 'user'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-900'
              }`}
            >
              <div className="whitespace-pre-wrap text-sm">{message.content}</div>
              
              {/* Action Buttons */}
              {message.actions && (
                <div className="mt-3 space-y-2">
                  {message.actions.map((action, index) => (
                    <button
                      key={index}
                      onClick={() => handleActionClick(action)}
                      className="block w-full text-left px-3 py-2 bg-white text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm border"
                    >
                      {action}
                    </button>
                  ))}
                </div>
              )}
              
              <div className="text-xs opacity-70 mt-2">
                {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
          </div>
        ))}

        {isTyping && (
          <div className="flex justify-start">
            <div className="bg-gray-100 p-3 rounded-2xl">
              <div className="flex space-x-1">
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Suggestions */}
      {suggestions.length > 0 && (
        <div className="p-3 border-t border-gray-100">
          <p className="text-xs text-gray-500 mb-2">Suggestions:</p>
          <div className="flex flex-wrap gap-2">
            {suggestions.slice(0, 2).map((suggestion, index) => (
              <button
                key={index}
                onClick={() => handleSuggestionClick(suggestion)}
                className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-2 py-1 rounded-full transition-colors"
              >
                {suggestion}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input */}
      <div className="p-4 border-t border-gray-200">
        <div className="flex space-x-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask me anything about your slides..."
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
          />
          <button
            onClick={handleSendMessage}
            disabled={!input.trim() || isTyping}
            className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <SafeIcon icon={FiSend} className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  )
}

export default AIAssistant